<script setup lang="ts">
import type { FrontPageCmsData } from '~/utils/crystallize/front-page'
import { faArrowRightFromBracket } from '@fortawesome/pro-duotone-svg-icons'

// FIXME: In theory, this inline type should be replacable with a Pick, since all field names are the same, but Vue doesn't like that for some reason.
const props = defineProps<{
  title: FrontPageCmsData['components']['title']
  leadParagraph: FrontPageCmsData['components']['leadParagraph']
  headerImage: FrontPageCmsData['components']['headerImage']
  primaryAction: FrontPageCmsData['components']['topSectionAction']
  secondaryAction: FrontPageCmsData['components']['secondaryTopSectionAction']
}>()

const router = useRouter()

const PARAM_PATTERN = /:(\w+)\(\)/g

function resolveCta(action: FrontPageCmsData['components']['topSectionAction'] | FrontPageCmsData['components']['secondaryTopSectionAction']) {
  const value = action?.action
  const label = action?.label?.trim()

  if (!value || !label) {
    return null
  }

  if (typeof value === 'string') {
    return { label, to: value, external: !value.startsWith('/') }
  }

  const name = value[0]?.value
  if (!name) {
    return null
  }

  const resolved = router.getRoutes().find(it => it.name === name)
  if (!resolved || resolved.path.match(PARAM_PATTERN)) {
    return null
  }

  return { label, to: resolved }
}

const resolvedPrimary = computed(() => resolveCta(props.primaryAction))
const resolvedSecondary = computed(() => resolveCta(props.secondaryAction))
</script>

<template>
  <section class="grid grid-cols-subgrid col-span-full items-center mb-l md:mb-3xl gap-y-m mt-l">
    <div class="col-span-full order-2 lg:col-span-4 2xl:col-span-6 flex flex-col gap-4 md:gap-7 text-center lg:text-start place-items-center lg:place-items-start">
      <h1 v-if="title" class="title-xxl" v-text="title" />

      <CrystallizeRichText
        v-for="(block, i) in leadParagraph"
        :key="i"
        :block="block"
        class="title-m !font-light"
      />

      <div v-if="resolvedPrimary || resolvedSecondary" class="flex flex-col sm:flex-row gap-xs mt-s md:mt-7">
        <Button v-if="resolvedPrimary" :icon="faArrowRightFromBracket" icon-position="left" variant="primary" color="moss" :to="resolvedPrimary.to" :external="resolvedPrimary.external">
          {{ resolvedPrimary.label }}
        </Button>
        <Button v-if="resolvedSecondary" variant="secondary" color="moss" :to="resolvedSecondary.to" :external="resolvedSecondary.external">
          {{ resolvedSecondary.label }}
        </Button>
      </div>
    </div>

    <CrystallizeImage
      v-if="headerImage"
      class="ml-4 col-span-full order-1 lg:order-3 sm:col-span-2 sm:col-start-2 lg:col-span-3 lg:col-start-6 2xl:col-start-8 2xl:col-span-5"
      img-class="object-cover object-center max-h-[500px] size-full rounded-4xl"
      :image="headerImage"
    />
  </section>
</template>
